import md5, {fromByteArray} from './md5';
import ajax from './ajax';
import Apis from "@common/apis";
import crypto from 'crypto';

export interface OssStsToken {
    ossEndpoint: string;
    ossRegion: string;
    ossBucket: string;
    accessKeyId: string;
    accessKeySecret: string;
    securityToken: string;
}

const DEFAULT_OSS_STS_TOKEN_TIMEOUT = (process.env.ENV_OSS_STS_TOKEN_TIMEOUT || (25 * 60 * 1000)) as number;

export class OssKit {
    public readonly stsTokenTimout: number;

    private stsToken: OssStsToken | null = null;
    private stsTokenRetrieveTime: number = -1;

    constructor(
        others?: {
            stsTokenTimeout?: number,
        }) {

        const {
            stsTokenTimeout = DEFAULT_OSS_STS_TOKEN_TIMEOUT
        } = others || {};

        this.stsTokenTimout = stsTokenTimeout;
    }

    public async uploadFile(blob: Blob, fileLocation: string, directAuthToken?: string): Promise<string> {
        return new Promise<string>(async (resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async (evt) => {
                console.log('start to upload file:', fileLocation);
                await this.fetchOssStsToken(directAuthToken);
                if (!this.stsToken) {
                    reject('获取STS Token失败');
                }
                const content = (evt.target! as any).result;
                // @ts-ignore
                await this.doUploadFile(content, `rmcp_${process.env.REACT_APP_ENV_NAME.toLowerCase()}/${fileLocation}`, this.stsToken!)
                    .then(url => {
                        console.info('file uploaded.', fileLocation);
                        resolve(url);
                    })
                    .catch(error => {
                        reject(error);
                    });
            };
            reader.readAsArrayBuffer(blob);
        })
    }

    private async fetchOssStsToken(directAuthToken?: string): Promise<void> {
        if (this.stsTokenRetrieveTime === -1 || (Date.now() - this.stsTokenRetrieveTime > this.stsTokenTimout)) {
            const currentTime = Date.now();
            await this.askOssStsToken(true, directAuthToken).then(ossStsToken => {
                this.stsToken = ossStsToken;
            }).catch(error => {
                console.error(error);
            });
            this.stsTokenRetrieveTime = currentTime;
            console.log('Oss sts token retrieved:', this.stsToken);
        }
    };

    private getUploadType = (name: string) : string => {
        const endName = name.substring(name.lastIndexOf('.') + 1);
        let type = 'text/plain';
        switch (endName.toLowerCase()) {
            case 'pdf':
                type = 'application/pdf';
                break;
            case 'jpg':
                type = 'image/jpeg';
                break;
            case 'jpeg':
                type = 'image/jpeg';
                break;
            case 'png':
                type = 'image/png';
                break;
            default:

        }
        return type;
    };

    private doUploadFile = async (file: Buffer, fileName: string, stsToken: OssStsToken): Promise<string> => {
        return new Promise<string>((resolve, reject) => {
            // @ts-ignore
            const date = new Date().toGMTString();
            const host = `${stsToken.ossBucket}.${stsToken.ossRegion}.aliyuncs.com`;
            const filepath = fileName.split('/').map(segment => encodeURIComponent(segment)).join('/');
            const url = `https://${host}/${filepath}`;
            console.log(url);

            if (this.hasWX()) {
                const bodyString = file.toString("utf8");
                const bodyMd5 = md5(bodyString);
                const bodyMd5Base64 = fromByteArray(bodyMd5);
                const conentType = `${this.getUploadType(fileName)};charset=utf-8`;

                const headers1 = {
                    'content-length': `${file.length}`,
                    'content-md5': bodyMd5Base64,
                    'Content-Type': conentType,
                    host: host,
                    // GMT time
                    'x-oss-date': date,
                    'x-oss-security-token': stsToken.securityToken,
                };
                const request = {
                    headers: headers1
                };

                const canonicalString = this.buildCanonicalString('PUT', `/${stsToken.ossBucket}/${fileName}`, request);
                console.log(`canonicalString = ${canonicalString}`);
                const signature = this.computeSignature(stsToken.accessKeySecret, canonicalString, "utf-8");
                const authorization = `OSS ${stsToken.accessKeyId}:${signature}`;

                const headers = {
                    'content-length': `${file.length}`,
                    'content-type': conentType,
                    'content-md5': bodyMd5Base64,
                    host: host,
                    // GMT time
                    'x-oss-date': date,
                    'x-oss-security-token': stsToken.securityToken,
                    authorization: authorization
                };

                // 微信小程序环境
                // @ts-ignore
                wx.request({
                    url,
                    method: 'PUT',
                    header: headers,
                    data: bodyString,
                    success: (res: { statusCode: number }) => {
                        const {statusCode} = res;
                        if (statusCode === 200) {
                            // 微信小程序环境，response格式待确定，暂不支持
                            console.error('微信小程序环境，response格式待确定，暂不支持');
                            resolve('');
                        } else {
                            reject();
                        }
                    },
                    fail: () => {
                        reject();
                    }
                });
            } else {
                // 一般浏览器环境
                //const bodyMd5 = md5(file);
                //const bodyMd5Base64 = fromByteArray(bodyMd5);
                const bodyMd5Base64 = crypto
                    .createHash('md5')
                    // @ts-ignore
                    .update(Buffer.from(file, 'utf8'))
                    .digest('base64');
                console.log(`bodyMd5Base64 [${bodyMd5Base64}]`);
                const conentType = `${this.getUploadType(fileName)}`;

                const headers = {
                    'content-length': `${file.length}`,
                    'content-md5': bodyMd5Base64,
                    'Content-Type': conentType,
                    host: host,
                    // GMT time
                    'x-oss-date': date,
                    'x-oss-security-token': stsToken.securityToken,
                    authorization: ''
                };

                const canonicalString = this.buildCanonicalString('PUT',
                    `/${stsToken.ossBucket}/${fileName}`, {headers: headers});
                console.log(`canonicalString = ${canonicalString}`);
                const signature = this.computeSignature(stsToken.accessKeySecret, canonicalString, "utf-8");
                headers.authorization = `OSS ${stsToken.accessKeyId}:${signature}`;

                fetch(url, {
                    method: 'PUT',
                    headers,
                    body: file
                }).then(response => {
                    if (response.status === 200) {
                        resolve(response.url);
                    } else {
                        reject('上传影像失败');
                    }
                }).catch(error => {
                    console.error(error);
                    reject('上传影像失败');
                });
            }
        });
    };

    private askOssStsToken = (isWrite: boolean, directAuthToken?: string): Promise<OssStsToken> => {
        return new Promise<OssStsToken>((resolve, reject) => {
            let url = `${Apis.STS_ASK_TOKEN}?isWrite=${isWrite}`;
            let isIgnoreAuth = false;
            if (directAuthToken) {
                url += `&${directAuthToken}`;
                isIgnoreAuth = true;
            }
            ajax.get(url, null, {ignoreAuth: isIgnoreAuth})
                .then(response => {
                    if (response) {
                        if (response.body) {
                            if (response.body.returnCode === 'RC-00001') {
                                resolve(response.body.body);
                            } else {
                                reject(response.body.errors[0]);
                            }
                        } else {
                            reject(response.error);
                        }
                    } else {
                        reject('获取STS Token失败');
                    }
                })
                .catch(error => {
                    reject(error);
                });
        });
    };

    private hasWX = () => {
        try {
            // @ts-ignore
            return wx && wx.request && (typeof wx.request === 'function');
        } catch {
            return false;
        }
    };

    private computeSignature = (accessKeySecret: string, canonicalString: string, headerEncoding: BufferEncoding) : string => {
        const signature = crypto.createHmac('sha1', accessKeySecret);
        return signature.update(Buffer.from(canonicalString, headerEncoding)).digest('base64');
    };

    private buildCanonicalString = (method: string, resourcePath: string, request: any) => {
        request = request || {};
        const headers = this.lowercaseKeyHeader(request.headers);
        const OSS_PREFIX = 'x-oss-';
        // @ts-ignore
        const ossHeaders = [];
        const headersToSign = {};

        let signContent = [
            method.toUpperCase(),
            // @ts-ignore
            headers['content-md5'] || '',
            // @ts-ignore
            headers['content-type'],
            // @ts-ignore
            headers['x-oss-date']
        ];

        Object.keys(headers).forEach((key) => {
            const lowerKey = key.toLowerCase();
            if (lowerKey.indexOf(OSS_PREFIX) === 0) {
                // @ts-ignore
                headersToSign[lowerKey] = String(headers[key]).trim();
            }
        });

        Object.keys(headersToSign).sort().forEach((key) => {
            // @ts-ignore
            ossHeaders.push(`${key}:${headersToSign[key]}`);
        });
        // @ts-ignore
        signContent = signContent.concat(ossHeaders);

        signContent.push(this.buildCanonicalizedResource(resourcePath, request.parameters));

        return signContent.join('\n');
    };

    private isObject = (obj: any) => {
        return Object.prototype.toString.call(obj) === '[object Object]';
    };

    private isArray = (obj: any) => {
        return Object.prototype.toString.call(obj) === '[object Array]';
    };

    private lowercaseKeyHeader = (headers: object) : object => {
        const lowercaseHeader = {};
        if (this.isObject(headers)) {
            Object.keys(headers).forEach(key => {
                // @ts-ignore
                lowercaseHeader[key.toLowerCase()] = headers[key];
            });
        }
        return lowercaseHeader;
    };

    private buildCanonicalizedResource = (resourcePath: string, parameters: any) : string => {
        let canonicalizedResource = `${resourcePath}`;
        let separatorString = '?';

        if (typeof parameters === 'string' && parameters.trim() !== '') {
            canonicalizedResource += separatorString + parameters;
        } else if (this.isArray(parameters)) {
            parameters.sort();
            canonicalizedResource += separatorString + parameters.join('&');
        } else if (parameters) {
            const compareFunc = (entry1: any, entry2: any) => {
                if (entry1[0] > entry2[0]) {
                    return 1;
                } else if (entry1[0] < entry2[0]) {
                    return -1;
                }
                return 0;
            };
            const processFunc = (key: any) => {
                canonicalizedResource += separatorString + key;
                if (parameters[key]) {
                    canonicalizedResource += `=${parameters[key]}`;
                }
                separatorString = '&';
            };
            Object.keys(parameters).sort(compareFunc).forEach(processFunc);
        }

        return canonicalizedResource;
    };

}